import { useState } from "react";
import { motion } from "framer-motion";
import NetworkIcon from "@core/network-icon";
import { AIFlowchartContent } from "../../types/ai-templates";

interface FlowchartTemplate2Props {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}

export function FlowchartTemplate2({
  content,
  onUpdate,
}: FlowchartTemplate2Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle step update
  const handleStepUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Ensure at least two steps exist
  if (!content.steps || content.steps.length < 2) {
    return (
      <div className="p-4 text-center text-error">
        At least two steps are required for FlowchartTemplate2.
      </div>
    );
  }

  // Limit to first two steps for text and icons
  const steps = content.steps.slice(0, 2);

  return (
    <motion.div
      className="overflow-hidden max-w-3xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* SVG Graphic */}
      <svg
        width="100%"
        style={{
          maxWidth: "1100px",
          height: "auto",
          fill: "none",
          stroke: "none",
          fillRule: "evenodd",
          clipRule: "evenodd",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          strokeMiterlimit: 1.5,
        }}
        viewBox="0 0 648 516"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Font Import */}
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
            text.description-text {
              letter-spacing: 0.02em; /* Adjust letter spacing for readability */
            }
            .description-div {
              font-family: 'Roboto', sans-serif;
              font-size: 18px;
              color: #171717;
              line-height: 1.3; /* Improved line height for readability */
              word-wrap: break-word; /* Ensure proper wrapping */
              white-space: normal; /* Allow wrapping */
            }
          `}
        </style>

        {/* Definitions */}
        <defs>
          <linearGradient id="gradient-8a8a8a-330675446" x2="0" y2="1">
            <stop offset="0" stop-color="#959595" />
            <stop offset="1" stop-color="#7f7f7f" />
          </linearGradient>
          <pattern
            id="crawlingPattern-5757571a20"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#5757571a20"
          >
            <use href="#crawlingSymbol" />
          </pattern>
          <symbol id="crawlingSymbol">
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                from="0 0"
                to="-12 0"
                dur="0.4s"
                repeatCount="indefinite"
              />
              <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z" />
            </g>
          </symbol>
          <linearGradient id="gradient-87aa3f-469929121" x2="0" y2="1">
            <stop offset="0" stop-color="#91bb39" />
            <stop offset="1" stop-color="#7d9844" />
          </linearGradient>
          <pattern
            id="crawlingPattern-c2f45b1a20"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#c2f45b1a20"
          >
            <use href="#crawlingSymbol" />
          </pattern>
          <linearGradient id="gradient-2898bd-56937253" x2="0" y2="1">
            <stop offset="0" stop-color="#1fa9d7" />
            <stop offset="1" stop-color="#3187a3" />
          </linearGradient>
          <pattern
            id="crawlingPattern-36cdff2120"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#36cdff2120"
          >
            <use href="#crawlingSymbol" />
          </pattern>
        </defs>

        {/* Shape 1 */}
        <g transform="translate(308.6300048828125, 143.0800018310547)">
          <g stroke="none" fill="url(#gradient-8a8a8a-330675446)">
            <path d="M 53.72 19.02L 47 29.47L 46.97 29.47L 46.96 29.46L 10 20.44L 16.72 10L 53.72 19.02ZM 114.27 57.99L 107.48 68.49L 88.18 55.97L 78.45 49.72L 47 29.47L 53.72 19.02L 114.27 57.99ZM 137.82 87.92L 131.11 98.36L 131.1 98.36L 107.48 68.49L 114.27 57.99L 137.8 87.89L 137.8 87.9L 137.82 87.92Z" />
          </g>
          <g stroke="none" fill="url(#crawlingPattern-5757571a20)">
            <path d="M 53.72 19.02L 47 29.47L 46.97 29.47L 46.96 29.46L 10 20.44L 16.72 10L 53.72 19.02ZM 114.27 57.99L 107.48 68.49L 88.18 55.97L 78.45 49.72L 47 29.47L 53.72 19.02L 114.27 57.99ZM 137.82 87.92L 131.11 98.36L 131.1 98.36L 107.48 68.49L 114.27 57.99L 137.8 87.89L 137.8 87.9L 137.82 87.92Z" />
          </g>
          <g
            fill="none"
            stroke="#f4f4f4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 53.72 19.02L 47 29.47L 46.97 29.47L 46.96 29.46L 10 20.44L 16.72 10L 53.72 19.02ZM 114.27 57.99L 107.48 68.49L 88.18 55.97L 78.45 49.72L 47 29.47L 53.72 19.02L 114.27 57.99ZM 137.82 87.92L 131.11 98.36L 131.1 98.36L 107.48 68.49L 114.27 57.99L 137.8 87.89L 137.8 87.9L 137.82 87.92Z" />
          </g>
          <g
            fill="none"
            stroke="#3131311a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 53.72 19.02L 47 29.47L 46.97 29.47L 46.96 29.46L 10 20.44L 16.72 10L 53.72 19.02ZM 114.27 57.99L 107.48 68.49L 88.18 55.97L 78.45 49.72L 47 29.47L 53.72 19.02L 114.27 57.99ZM 137.82 87.92L 131.11 98.36L 131.1 98.36L 107.48 68.49L 114.27 57.99L 137.8 87.89L 137.8 87.9L 137.82 87.92Z" />
          </g>
        </g>

        {/* Shape 2 */}
        <g transform="translate(218.60000610351562, 294.260009765625)">
          <g stroke="none" fill="url(#gradient-8a8a8a-330675446)">
            <path d="M 131.3 87.61L 62.24 115.22C 54.96 96.99 37.11 84.14 16.29 84.21C 15.48 84.21 14.68 84.23 13.88 84.28L 10 10L 47.02 18.93L 107.67 57.73L 131.3 87.61Z" />
          </g>
          <g stroke="none" fill="url(#crawlingPattern-5757571a20)">
            <path d="M 131.3 87.61L 62.24 115.22C 54.96 96.99 37.11 84.14 16.29 84.21C 15.48 84.21 14.68 84.23 13.88 84.28L 10 10L 47.02 18.93L 107.67 57.73L 131.3 87.61Z" />
          </g>
          <g
            fill="none"
            stroke="#f4f4f4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 131.3 87.61L 62.24 115.22C 54.96 96.99 37.11 84.14 16.29 84.21C 15.48 84.21 14.68 84.23 13.88 84.28L 10 10L 47.02 18.93L 107.67 57.73L 131.3 87.61Z" />
          </g>
          <g
            fill="none"
            stroke="#3131311a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 131.3 87.61L 62.24 115.22C 54.96 96.99 37.11 84.14 16.29 84.21C 15.48 84.21 14.68 84.23 13.88 84.28L 10 10L 47.02 18.93L 107.67 57.73L 131.3 87.61Z" />
          </g>
        </g>

        {/* Shape 3 */}
        <g transform="translate(222.4810028076172, 121.99799346923828)">
          <g stroke="none" fill="url(#gradient-8a8a8a-330675446)">
            <path d="M 58.36 287.482008L 12.57 305.782008L 10 256.542008C 10.8 256.492008 11.6 256.472008 12.41 256.472008C 33.23 256.402008 51.08 269.252008 58.36 287.482008ZM 223.97 108.991708L 228.92 101.261708C 236 90.191708 234.94 75.781708 226.31 65.861708C 203.66 39.851708 174.03 20.881708 140.93 11.211708L 140.86 11.191708C 128.24 7.501708 114.7 12.581708 107.62 23.651708L 102.87 31.081708L 139.87 40.101708L 200.42 79.061708L 223.97 108.981708L 223.97 108.991708Z" />
          </g>
          <g stroke="none" fill="url(#crawlingPattern-5757571a20)">
            <path d="M 58.36 287.482008L 12.57 305.782008L 10 256.542008C 10.8 256.492008 11.6 256.472008 12.41 256.472008C 33.23 256.402008 51.08 269.252008 58.36 287.482008ZM 223.97 108.991708L 228.92 101.261708C 236 90.191708 234.94 75.781708 226.31 65.861708C 203.66 39.851708 174.03 20.881708 140.93 11.211708L 140.86 11.191708C 128.24 7.501708 114.7 12.581708 107.62 23.651708L 102.87 31.081708L 139.87 40.101708L 200.42 79.061708L 223.97 108.981708L 223.97 108.991708Z" />
          </g>
          <g
            fill="none"
            stroke="#f4f4f4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 58.36 287.481708L 12.57 305.781708L 10 256.541708C 10.8 256.491708 11.6 256.471708 12.41 256.471708C 33.23 256.401708 51.08 269.251708 58.36 287.481708ZM 223.97 108.991708L 228.92 101.261708C 236 90.191708 234.94 75.781708 226.31 65.861708C 203.66 39.851708 174.03 20.881708 140.93 11.211708L 140.86 11.191708C 128.24 7.501708 114.7 12.581708 107.62 23.651708L 102.87 31.081708L 139.87 40.101708L 200.42 79.061708L 223.97 108.981708L 223.97 108.991708Z" />
          </g>
          <g
            fill="none"
            stroke="#3131311a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 58.36 287.481708L 12.57 305.781708L 10 256.541708C 10.8 256.491708 11.6 256.471708 12.41 256.471708C 33.23 256.401708 51.08 269.251708 58.36 287.481708ZM 223.97 108.991708L 228.92 101.261708C 236 90.191708 234.94 75.781708 226.31 65.861708C 203.66 39.851708 174.03 20.881708 140.93 11.211708L 140.86 11.191708C 128.24 7.501708 114.7 12.581708 107.62 23.651708L 102.87 31.081708L 139.87 40.101708L 200.42 79.061708L 223.97 108.981708L 223.97 108.991708Z" />
          </g>
        </g>

        {/* Casual Gardeners Shape (First Step) */}
        <g transform="translate(218.60000610351562, 224.0500030517578)">
          <g stroke="none" fill="url(#gradient-87aa3f-469929121)">
            <path d="M 176.22 87.6L 131.3 157.82L 107.67 127.94L 152.59 57.73L 176.22 87.6ZM 91.95 18.93L 47.02 89.14L 10 80.21L 54.92 10L 91.95 18.93ZM 152.59 57.73L 107.67 127.94L 47.02 89.14L 91.95 18.93L 152.59 57.73Z" />
          </g>
          <g stroke="none" fill="url(#crawlingPattern-c2f45b1a20)">
            <path d="M 176.22 87.6L 131.3 157.82L 107.67 127.94L 152.59 57.73L 176.22 87.6ZM 91.95 18.93L 47.02 89.14L 10 80.21L 54.92 10L 91.95 18.93ZM 152.59 57.73L 107.67 127.94L 47.02 89.14L 91.95 18.93L 152.59 57.73Z" />
          </g>
          <g
            fill="none"
            stroke="#f4f4f4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 176.22 87.6L 131.3 157.82L 107.67 127.94L 152.59 57.73L 176.22 87.6ZM 91.95 18.93L 47.02 89.14L 10 80.21L 54.92 10L 91.95 18.93ZM 152.59 57.73L 107.67 127.94L 47.02 89.14L 91.95 18.93L 152.59 57.73Z" />
          </g>
          <g
            fill="none"
            stroke="#3131311a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 176.22 87.6L 131.3 157.82L 107.67 127.94L 152.59 57.73L 176.22 87.6ZM 91.95 18.93L 47.02 89.14L 10 80.21L 54.92 10L 91.95 18.93ZM 152.59 57.73L 107.67 127.94L 47.02 89.14L 91.95 18.93L 152.59 57.73Z" />
          </g>
        </g>

        {/* Plant Enthusiasts Shape (Second Step) */}
        <g transform="translate(263.52001953125, 153.5189971923828)">
          <g stroke="none" fill="url(#gradient-2898bd-56937253)">
            <path d="M 176.22 87.92L 131.3 158.13L 107.67 128.26L 152.59 58.05L 176.22 87.92ZM 92.11 19.03L 47.02 89.46L 10 80.53L 55.11 10L 92.11 19.03ZM 152.59 58.05L 107.67 128.26L 47.02 89.46L 92.11 19.03L 152.59 58.05Z" />
          </g>
          <g stroke="none" fill="url(#crawlingPattern-36cdff2120)">
            <path d="M 176.22 87.92L 131.3 158.13L 107.67 128.26L 152.59 58.05L 176.22 87.92ZM 92.11 19.03L 47.02 89.46L 10 80.53L 55.11 10L 92.11 19.03ZM 152.59 58.05L 107.67 128.26L 47.02 89.46L 92.11 19.03L 152.59 58.05Z" />
          </g>
          <g
            fill="none"
            stroke="#f4f4f4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
          >
            <path d="M 176.22 87.92L 131.3 158.13L 107.67 128.26L 152.59 58.05L 176.22 87.92ZM 92.11 19.03L 47.02 89.46L 10 80.53L 55.11 10L 92.11 19.03ZM 152.59 58.05L 107.67 128.26L 47.02 89.46L 92.11 19.03L 152.59 58.05Z" />
          </g>
          <g
            fill="none"
            stroke="#3131311a"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="4"
            strokeDasharray="4,6"
          >
            <animate
              attributeName="stroke-dashoffset"
              values="30;0"
              dur="1s"
              repeatCount="indefinite"
              begin="0"
            />
            <path d="M 176.22 87.92L 131.3 158.13L 107.67 128.26L 152.59 58.05L 176.22 87.92ZM 92.11 19.03L 47.02 89.46L 10 80.53L 55.11 10L 92.11 19.03ZM 152.59 58.05L 107.67 128.26L 47.02 89.46L 92.11 19.03L 152.59 58.05Z" />
          </g>
        </g>

        {/* Main Title */}
        <g transform="translate(152, 50)">
          <g stroke="none" fill="#171717">
            <text
              style={{ font: "18px Roboto, sans-serif", whiteSpace: "pre" }}
              fontSize="18px"
              fontFamily="Roboto, sans-serif"
              className="description-text"
            >
              <tspan x="14.27" y="33.5" dominant-baseline="ideographic">
                {content.title || "Understanding Our Target Audience"}
              </tspan>
            </text>
          </g>
        </g>

        {/* First Step Text (Casual Gardeners) */}
        <g transform="translate(26, 122)">
          <g stroke="none" fill={steps[0].color || "#1ac3fb"}>
            <text
              style={{
                font: "bold 20px Roboto, sans-serif",
                whiteSpace: "pre",
                letterSpacing: "0.02em",
              }}
              fontWeight="bold"
              fontSize="20px"
              fontFamily="Roboto, sans-serif"
            >
              {editingIndex === 0 && editingField === "title" ? (
                <tspan x="16.6" y="33.5" dominant-baseline="ideographic">
                  <foreignObject x="16.6" y="10" width="200" height="40">
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== steps[0].title) {
                          handleStepUpdate(0, "title", tempValue);
                        }
                      }}
                      className="px-2 py-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary text-lg font-semibold text-white"
                      style={{ color: steps[0].color || "#1ac3fb" }}
                    />
                  </foreignObject>
                </tspan>
              ) : (
                <tspan
                  x="16.6"
                  y="33.5"
                  dominant-baseline="ideographic"
                  onClick={() => {
                    setEditingIndex(0);
                    setEditingField("title");
                    setTempValue(steps[0].title);
                  }}
                  className="cursor-text"
                >
                  {steps[0].title}
                </tspan>
              )}
            </text>
          </g>
        </g>

        {/* First Step Description */}
        <g transform="translate(26, 158)">
          <g stroke="none" fill="#171717">
            {editingIndex === 0 && editingField === "description" ? (
              <foreignObject x="12.9" y="10" width="170" height="100">
                <textarea
                  autoFocus
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== steps[0].description) {
                      handleStepUpdate(0, "description", tempValue);
                    }
                  }}
                  className="px-2 py-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary text-sm description-div"
                  style={{
                    color: "#171717",
                    maxWidth: "170px",
                    resize: "none",
                    height: "auto",
                  }}
                  rows={4}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = "auto";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                />
              </foreignObject>
            ) : (
              <foreignObject x="12.9" y="10" width="170" height="100">
                <div
                  className="description-div cursor-text"
                  style={{
                    maxWidth: "170px",
                    padding: "4px",
                  }}
                  onClick={() => {
                    setEditingIndex(0);
                    setEditingField("description");
                    setTempValue(steps[0].description);
                  }}
                >
                  {steps[0].description}
                </div>
              </foreignObject>
            )}
          </g>
        </g>

        {/* Second Step Text (Plant Enthusiasts) */}
        <g transform="translate(410, 314)">
          <g stroke="none" fill={steps[1].color || "#a6da37"}>
            <text
              style={{
                font: "bold 20px Roboto, sans-serif",
                whiteSpace: "pre",
                letterSpacing: "0.02em",
              }}
              fontWeight="bold"
              fontSize="20px"
              fontFamily="Roboto, sans-serif"
            >
              {editingIndex === 1 && editingField === "title" ? (
                <tspan x="12" y="33.5" dominant-baseline="ideographic">
                  <foreignObject x="12" y="10" width="200" height="40">
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== steps[1].title) {
                          handleStepUpdate(1, "title", tempValue);
                        }
                      }}
                      className="px-2 py-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary text-lg font-semibold text-white"
                      style={{ color: steps[1].color || "#a6da37" }}
                    />
                  </foreignObject>
                </tspan>
              ) : (
                <tspan
                  x="12"
                  y="33.5"
                  dominant-baseline="ideographic"
                  onClick={() => {
                    setEditingIndex(1);
                    setEditingField("title");
                    setTempValue(steps[1].title);
                  }}
                  className="cursor-text"
                >
                  {steps[1].title}
                </tspan>
              )}
            </text>
          </g>
        </g>

        {/* Second Step Description */}
        <g transform="translate(410, 350)">
          <g stroke="none" fill="#171717">
            {editingIndex === 1 && editingField === "description" ? (
              <foreignObject x="12" y="10" width="170" height="100">
                <textarea
                  autoFocus
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  onBlur={() => {
                    setEditingIndex(null);
                    setEditingField(null);
                    if (tempValue !== steps[1].description) {
                      handleStepUpdate(1, "description", tempValue);
                    }
                  }}
                  className="px-2 py-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary text-sm description-div"
                  style={{
                    color: "#171717",
                    maxWidth: "170px",
                    resize: "none",
                    height: "auto",
                  }}
                  rows={4}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = "auto";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                />
              </foreignObject>
            ) : (
              <foreignObject x="12" y="10" width="170" height="100">
                <div
                  className="description-div cursor-text"
                  style={{
                    maxWidth: "170px",
                    padding: "4px",
                  }}
                  onClick={() => {
                    setEditingIndex(1);
                    setEditingField("description");
                    setTempValue(steps[1].description);
                  }}
                >
                  {steps[1].description}
                </div>
              </foreignObject>
            )}
          </g>
        </g>

        {/* First Step Icon */}
        <g transform="translate(332, 195)">
          {steps[0].iconId ? (
            <foreignObject x="0" y="0" width="60" height="60">
              <div className="flex items-center justify-center h-full">
                <NetworkIcon
                  value={steps[0].icon_description ?? steps[0].iconId}
                  type="LUCIDE"
                  className="w-10 h-10 text-white"
                />
              </div>
            </foreignObject>
          ) : (
            <g
              fill="none"
              stroke="#f4f4f4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
            >
              <path d="M 51.125 48.771252L 10.875 48.771252M 38.873253 48.771252L 38.873253 38.021M 41.517502 19.02125C 40.145725 20.822742 40.375206 23.37326 42.046524 24.900953C 43.717842 26.428646 46.27866 26.428646 47.949978 24.900953C 49.621292 23.37326 49.850777 20.822742 48.479 19.02125ZM 48.5 19.02125L 40.625 19.02125L 38.664997 14.4485C 38.536133 14.147942 38.585621 13.800608 38.793304 13.548C 39.000984 13.295394 39.332195 13.17968 39.652 13.248L 49.144001 15.2815C 49.408524 15.338032 49.63203 15.513728 49.749428 15.757422C 49.866829 16.001116 49.864914 16.285408 49.744251 16.5275ZM 40.623253 19.02125L 37.123249 20.77125M 38.875 38.021L 34.0345 44.146C 33.207073 45.338367 31.569771 45.63435 30.377256 44.807129C 29.184744 43.979908 28.888483 42.342655 29.7155 41.149998L 37.5905 30.65C 38.093597 29.921709 38.929855 29.496014 39.814751 29.51775L 41.877998 29.51775C 42.1856 29.517685 42.470642 29.679144 42.62875 29.943001L 44.249249 32.64325C 44.40728 32.907227 44.692337 33.068806 45 33.068806C 45.307663 33.068806 45.59272 32.907227 45.750751 32.64325L 47.369499 29.943001C 47.527607 29.679144 47.812649 29.517685 48.120251 29.51775L 49.375 29.51775C 49.971153 29.516674 50.56308 29.617891 51.125 29.817001M 19.625 48.771252L 19.625 31.27125M 24.875 36.521248C 22.81834 36.558353 20.888678 37.523182 19.625 39.146252M 23.125 33.021248C 23.125 33.021248 20.5 33.021248 19.625 34.771248M 12.625 40.021252C 15.544031 40.214466 18.172197 41.857071 19.625 44.396252M 26.625 40.021252C 23.705969 40.214466 21.077803 41.857071 19.625 44.396252M 14.375 36.521248C 16.43166 36.558353 18.361322 37.523182 19.625 39.146252M 16.125 33.021248C 16.125 33.021248 18.75 33.021248 19.625 34.771248" />
            </g>
          )}
        </g>

        {/* Second Step Icon */}
        <g transform="translate(288, 265)">
          {steps[1].iconId ? (
            <foreignObject x="0" y="0" width="60" height="60">
              <div className="flex items-center justify-center h-full">
                <NetworkIcon
                  value={steps[1].icon_description ?? steps[1].iconId}
                  type="LUCIDE"
                  className="w-10 h-10 text-white"
                />
              </div>
            </foreignObject>
          ) : (
            <g
              fill="none"
              stroke="#f4f4f4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="4"
            >
              <path d="M 39.228325 29.566225C 41.64455 29.566225 43.603325 27.607624 43.603325 25.191313C 43.603325 22.77507 41.64455 20.816313 39.228325 20.816313C 36.812099 20.816313 34.853325 22.77507 34.853325 25.191313C 34.853325 27.607624 36.812099 29.566225 39.228325 29.566225ZM 10 10M 40.978325 35.691402L 35.378326 38.8414C 34.153324 39.541401 32.578327 39.191399 31.878325 37.9664C 31.178326 36.741402 31.528324 35.166401 32.753326 34.4664L 40.278328 29.741224C 43.778328 27.991224 46.403328 29.741224 48.153328 32.366226C 49.903328 34.991226 49.903328 38.491226 49.903328 41.991226C 49.903328 46.366226 44.653328 48.116226 42.028328 44.616226M 36.603325 42.691227L 36.603325 46.191227C 36.603325 47.591225 37.828327 48.816227 39.228325 48.816227C 40.628326 48.816227 41.853325 47.591225 41.853325 46.191227L 41.853325 42.691227M 28.371675 22.108688C 30.471676 20.008688 30.471676 16.683687 28.371675 14.758687C 26.271641 12.658688 22.946623 12.658688 21.021622 14.758687C 18.921623 12.658688 15.596622 12.658688 13.671622 14.758687C 11.57163 16.858688 11.57163 20.183687 13.671622 22.108688C 11.57163 24.208689 11.57163 27.5336 13.671622 29.458599C 15.771623 31.3836 19.096622 31.558599 21.021622 29.458599C 23.121622 31.558599 26.44664 31.558599 28.371675 29.458599C 30.471676 27.358686 30.471676 24.208689 28.371675 22.108688ZM 21.021622 24.733669C 22.471357 24.733669 23.646622 23.558422 23.646622 22.108669C 23.646622 20.658936 22.471357 19.483669 21.021622 19.483669C 19.571869 19.483669 18.396622 20.658936 18.396622 22.108669C 18.396622 23.558422 19.571869 24.733669 21.021622 24.733669ZM 21.021622 29.458599L 21.021622 38.383598M 25.396622 46.258598L 16.646622 46.258598L 15.771623 38.383598L 26.271622 38.383598L 25.396622 46.258598Z" />
            </g>
          )}
        </g>
      </svg>
    </motion.div>
  );
}
